import { DISTRICTS } from "@/lib/districts";
import { formatProjectName, formatPropertyName } from "@/lib/utils";
import type { PropertyCardV2Props } from "@/components/v2/PropertyCardV2";
import { sanePrice, tenureLabel, topYear } from "./format";

/** The subset of a property record the list endpoint returns and the cards need. */
export type PropertyLite = {
  _id: string;
  name: string;
  title?: string;
  description?: string;
  /** Bedroom counts (1-5) the description or title mentions; see bedsHint. */
  beds_hint?: number[];
  full_address?: string;
  pin_code?: string;
  district?: string;
  construction_status?: string;
  isPreLaunch?: boolean;
  is_leasehold?: boolean;
  lease_year?: number;
  units?: number;
  min_flat_price?: number | null;
  psf_from?: number | null;
  expected_top?: string | null;
  launch_date?: string | null;
  is_feature_property?: boolean;
  /** Home page positions set in the admin, 1 = first, null = not listed. */
  launching_soon_order?: number | null;
  now_selling_order?: number | null;
  createdAt?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    coordinates?: [number, number];
  } | null;
  images?: { property_images?: string[] };
};

type GroupedResponse = {
  total?: number;
  results?: Record<string, PropertyLite[]> | PropertyLite[];
};

const API_BASE =
  process.env.API_URL ||
  process.env.NEXT_PUBLIC_API_URL ||
  process.env.NEXT_PUBLIC_BACKEND_URL ||
  "";

export function flattenGrouped(data: GroupedResponse | null | undefined): PropertyLite[] {
  if (!data?.results) return [];
  if (Array.isArray(data.results)) return data.results;
  const seen = new Set<string>();
  const out: PropertyLite[] = [];
  for (const list of Object.values(data.results)) {
    for (const p of list) {
      if (!p?._id || seen.has(p._id)) continue;
      seen.add(p._id);
      out.push(p);
    }
  }
  return out;
}

/** Server-side list fetch. Cached 5 minutes; returns [] on any failure. */
export async function fetchAllProperties(): Promise<PropertyLite[]> {
  if (!API_BASE) return [];
  try {
    const res = await fetch(`${API_BASE}/property`, { next: { revalidate: 300 } });
    if (!res.ok) return [];
    return flattenGrouped((await res.json()) as GroupedResponse);
  } catch {
    return [];
  }
}

export function propertyHref(p: Pick<PropertyLite, "name">): string {
  return `/single-property/${formatPropertyName(p.name)}`;
}

export function propertyImage(p: PropertyLite): string | null {
  const first = p.images?.property_images?.[0];
  const base = process.env.NEXT_PUBLIC_IMAGE_URL;
  if (!first || !base) return null;
  if (/^https?:\/\//i.test(first)) return first;
  return `${base}/${formatProjectName(p.name)}/property_images/${first}`;
}

export function propertyCoords(p: PropertyLite): { lat: number; lng: number } | null {
  const loc = p.location;
  if (!loc) return null;
  if (typeof loc.latitude === "number" && typeof loc.longitude === "number" && loc.latitude !== 0) {
    return { lat: loc.latitude, lng: loc.longitude };
  }
  if (Array.isArray(loc.coordinates) && loc.coordinates.length === 2 && loc.coordinates[1] !== 0) {
    return { lng: loc.coordinates[0], lat: loc.coordinates[1] };
  }
  return null;
}

export function isSelling(p: PropertyLite): boolean {
  return p.construction_status === "Under Construction" || p.construction_status === "Move In";
}

export function toCardProps(p: PropertyLite): PropertyCardV2Props {
  const district = p.district || "";
  const districtName = district ? DISTRICTS[district]?.label : undefined;
  const address = p.full_address?.trim() || (p.pin_code ? `Postal ${p.pin_code}` : undefined);
  return {
    slug: formatPropertyName(p.name),
    name: (p.title || p.name).trim(),
    district,
    districtName,
    address,
    image: propertyImage(p),
    constructionStatus: p.construction_status || "Upcoming",
    isPreLaunch: !!p.isPreLaunch,
    priceFrom: sanePrice(p.min_flat_price),
    psfFrom: p.psf_from ?? null,
    unitsTotal: p.units && p.units > 0 ? p.units : null,
    unitsLeft: null,
    expectedTop: topYear(p.expected_top),
    tenure: tenureLabel(!!p.is_leasehold, p.lease_year),
    href: propertyHref(p),
  };
}

/** Bedroom counts (1-5) a free-text blurb mentions ("3-bed", "2 bedroom", "4BR"). */
export function bedsHint(text: string): number[] {
  const t = text.toLowerCase();
  return [1, 2, 3, 4, 5].filter((n) => new RegExp(`\\b${n}[ -]?(bed|br|bedroom)`, "i").test(t));
}
